import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, PlayCircle } from "@phosphor-icons/react";
import { CONTENT, IMAGES } from "../../data/content";
import { useLang } from "../../context/LanguageContext";
import { scrollToId } from "../../lib/api";
import { MaskLine } from "./Motion";

export const Hero = () => {
  const { lang } = useLang();
  const t = CONTENT[lang].hero;
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 600], [0, 120]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-28 pb-16">
      <div className="absolute inset-0 grid-bg opacity-60" />
      <motion.div style={{ y: yImg, opacity }} className="absolute -top-40 right-0 w-[70%] h-[120%] pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-l from-blue-600/20 to-transparent blur-[120px]" />
        <img src={IMAGES.hero} alt="" className="w-full h-full object-cover opacity-30 mix-blend-luminosity" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]" />
      </motion.div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 w-full">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 mb-8">
          <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
          <span className="text-xs font-mono uppercase tracking-widest text-zinc-300">{t.badge}</span>
        </motion.div>

        <h1 className="font-heading font-black tracking-tighter leading-[0.92] text-5xl sm:text-6xl lg:text-8xl text-white max-w-5xl">
          {t.titleLines.map((line, i) => (
            <MaskLine key={i} delay={0.15 + i * 0.12} className={i === t.titleLines.length - 1 ? "text-zinc-500" : ""}>
              {line}
            </MaskLine>
          ))}
        </h1>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 0.8 }}
          className="mt-8 text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
          {t.subtitle}
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }}
          className="mt-10 flex flex-col sm:flex-row gap-4">
          <button data-testid="hero-cta-consult" onClick={() => scrollToId("contatti")}
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-blue-500 text-white font-semibold px-7 py-4 hover:bg-blue-400 transition-colors duration-300">
            {CONTENT[lang].ctaConsult}
            <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
          </button>
          <button data-testid="hero-cta-demo" onClick={() => scrollToId("app")}
            className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-white font-semibold px-7 py-4 hover:bg-white/5 transition-colors duration-300">
            <PlayCircle size={20} weight="fill" className="text-blue-400" />
            {CONTENT[lang].ctaDemo}
          </button>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
          className="mt-16 flex flex-wrap gap-10 border-t border-white/10 pt-8 max-w-2xl">
          {t.stats.map((s) => (
            <div key={s.label}>
              <div className="font-heading font-black text-3xl md:text-4xl text-white">{s.value}</div>
              <div className="text-xs uppercase tracking-widest font-mono text-zinc-500 mt-1">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
